import { Component, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, NavigationError, RouteConfigLoadEnd, RouteConfigLoadStart, Router } from '@angular/router';
import { takeUntil } from 'rxjs/operators';
import { environment } from '@environment';
import { ICLientData } from '@models/client.model';
import { IUser } from '@models/user.model';
import { AuthService } from '@services/auth/auth.service';
import { EventsService } from '@services/events/events.service';
import { ScriptLoaderService } from '@services/script-loader/script-loader.service';
import { SeoService } from '@services/seo/seo.service';
import { UtilService } from '@services/util/util.service';
import { VariablesService } from '@services/variables/variables.service';
import { DefaultIPLocation } from '@models/default-ip';
import { StorageService } from '@services/storage/storage.service';
import { Subject } from 'rxjs';
import { ApiService } from '@services/api/api.service';
import { API_ENDTPOINTS } from '@models/api-endpoints';
import { EuropeanCountries, GulfCountries, SupportedCurrencies } from '@models/currency-list';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent {

  readonly actRoute = inject(ActivatedRoute);
  readonly router = inject(Router);
  private readonly api = inject(ApiService);
  private readonly auth = inject(AuthService);
  private readonly events = inject(EventsService);
  private readonly scriptLoader = inject(ScriptLoaderService);
  private readonly seo = inject(SeoService);
  private readonly storage = inject(StorageService);
  private readonly util = inject(UtilService);
  readonly vars = inject(VariablesService);

  private readonly destroy$ = new Subject<void>();

  loadingRouteConfig = false;

  qParams = this.util.getUrlParams();
  routeData: any = {};

  // reloadKey = 'chunk-load-reload-attempted';

  constructor() {
    console.log('production: ', environment.production);
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe(event => {
      if (event instanceof RouteConfigLoadStart) {
        this.loadingRouteConfig = true;
      } else if (event instanceof RouteConfigLoadEnd) {
        this.loadingRouteConfig = false;
      } else if (event instanceof NavigationEnd) {
        this.onNavigation(event);
        // StorageService wraps sessionStorage in try/catch – it throws SecurityError when storage is blocked
      //   this.storage.deleteFromSession(this.reloadKey);
      // } else if (event instanceof NavigationError) {
      //   this.loadingRouteConfig = false;
      //   this.handleNavigationError(event);
      }
    });

    if (this.vars.isBrowser && this.qParams?.utm_source?.match('internal_impact_calling') && !window.location.pathname.match('stories/') && !this.qParams?.to?.match('stories/')) {
      this.vars.isVariableLogin = true;
    }

    if (this.vars.isBrowser) {
      this.setUtmCookie();
      this.setDeviceType();
      this.getClientIP();
      this.setUser();
    }
  }

  ngOnInit() {
    const webPageSchema = {
      '@context': 'http://schema.org',
      '@id': 'https://www.ketto.org',
      '@type': 'WebPage',
      'url': 'https://www.ketto.org',
      'name': 'Ketto - Crowdfunding India'
    };
    this.seo.schemaOrgObject(webPageSchema);
    this.vars.listenToGlabalJsVar();
  }

  ngAfterViewInit() {
    if (!this.vars.isBrowser) return;
    this.scriptLoader.loadScript('gtm', '', true).then();
    this.scriptLoader.loadScript('clevertap', '', true);
    this.scriptLoader.loadScript('fb_pixel_script', '', true);
    this.scriptLoader.loadScript('microsoft_clarity', '', true);
    if (!this.router.url.match('stories|fundraiser|thankyou|pledge-more|sip|portfolio|payment')) {
      this.events.pushNotificationCL();
    }
    this.vars.userData.subscribe(res => {
      if (res?.id) {
        const newData = { ...res };
        if (this.vars.isAutoLogin) {
          newData.email = newData?.email?.includes('*') ? '' : newData.email;
          newData.extension = newData?.phone_1?.includes('*') ? '' : newData.extension;
          newData.phone_1 = newData?.phone_1?.includes('*') ? '' : newData.email;
        }
        this.events.clarityEventsPush(newData?.id);
        if (newData?.isDummyEmail) {
          this.vars.isDummyEmail = true;
        } else {
          this.vars.isDummyEmail = false;
        }
      }
    });
  }

  onNavigation(e: NavigationEnd) {
    this.vars.previousUrl = this.vars.currentUrl;
    this.vars.currentUrl = e.urlAfterRedirects;
    if (this.vars.previousUrl.split('?')[0] === this.vars.currentUrl.split('?')[0]) return;
    this.routeData = this.getRouteFirstChild().data;
    this.vars.pageName = this.routeData?.['page_name'] || '';
    this.util.setUtm();
    this.vars.onUrlChange$.next(e);
    if (!this.routeData?.['skipCanonical']) {
      this.seo.setCanonical();
    }
    if (this.vars.isBrowser) {
      this.gtmOnLoad(this.routeData);
      if (!this.routeData?.['skipLastOrder']) {
        this.getLastOrder();
      }
    }
  }

  // handleNavigationError(event: NavigationError) {
  //   const error = event.error;
  //   const message = error?.message || String(error);
  //   const isChunkLoadError = error?.name === 'ChunkLoadError' || message.includes('ChunkLoadError') || message.includes('Loading chunk');
  //   if (!isChunkLoadError || !this.vars.isBrowser) {
  //     return;
  //   }
  //   // Reload only once to prevent infinite loops. If storage is blocked the flag can't be saved,
  //   // so skip the reload rather than risk a reload loop.
  //   if (!this.storage.getFromSession(this.reloadKey)) {
  //     if (this.storage.addSessionData(this.reloadKey, { attempted: true })) {
  //       window.location.reload();
  //     }
  //     return;
  //   }
  //   // If reload already happened and chunk still fails,
  //   // allow normal error handling
  //   this.storage.deleteFromSession(this.reloadKey);
  // }

  getRouteFirstChild() {
    let route = this.actRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route.snapshot;
  }

  gtmOnLoad(routeData: any) {
    const user: IUser = this.util.getUserData();
    const currency = this.storage.getFromSession('currency');
    const gtmPush = {
      event: 'virtual_pageview',
      page_version: 'A',
      login_status: user ? 'logged in' : 'not logged in', // Populate either LOGGED IN or NOT LOGGED IN
      user_type: user?.entity_type || 'visitor',
      currency: currency, // Currency used
      user_id: user?.id || '',   // Identifies unique users across multiple devices
      page_category: routeData?.category || '',  // ex: home, crowdfunding, fundraiser etc
      page_name: routeData?.page_name || '',  // name of the page
      sip: routeData?.sip || false
    };
    this.vars.gtmPageData = gtmPush;
    this.events.gtmPush(gtmPush);
  }


  async getLastOrder() {
    if (!this.util.getUserToken()) return;
    const qparam = `?limit=1&orderBy=creation_date&sortedBy=desc`;
    const resData = await this.auth.getUserOrders(qparam)
    if (resData?.data && resData.data?.length) {
      this.vars.setLastOrderValues(resData.data[0]);
    }
  }

  setUtmCookie() {
    if (!this.vars.isBrowser) return;
    const cookieUtm: any = this.storage.getCookie('_telecaling') ? JSON?.parse(this.storage.getCookie('_telecaling')) : {};
    const utms: any = this.util.getUTMs();
    const agentSource = 'internal_stories_otd_calling|internal_impact_calling';
    if (cookieUtm?.utm_source?.match(agentSource) || utms?.utm_source?.match(agentSource)) {
      this.vars.isAgent = true;
    }
    if (!utms?.hasOwnProperty('utm_source')) {
      return;
    }
    utms.created_date = new Date().toISOString();
    const utmSource = utms?.utm_source?.toLowerCase();
    if (utmSource?.match('_calling')) {
      this.storage.setCookie('_telecaling', `${JSON.stringify(utms)}`, 7);
    } else if (utmSource?.match('_ct|_clevertap')) {
      this.storage.setCookie('_clevertap', `${JSON.stringify(utms)}`, 7);
    } else if (utmSource?.match('external_')) {
      this.storage.setCookie('_ad', `${JSON.stringify(utms)}`, 7);
    }
  }

  setDeviceType() {
    if (!this.vars.isBrowser) return;
    const platform = this.storage.getCookie('platform');
    if (platform) {
      this.vars.deviceType = platform;
    }
    if (this.qParams?.platform) {
      this.storage.addSessionData('platform', this.qParams?.platform);
    }
    const sesPlatform = this.storage.getFromSession('platform');
    if (sesPlatform) {
      this.vars.deviceType = sesPlatform;
    }
  }

  getClientIP() {
    const ip = this.storage.getFromSession('iplocation');
    if (!ip) {
      this.api.request('get', API_ENDTPOINTS.GET_IP).subscribe({
        next: (res: any) => {
          this.setClientIP(res.data);
        },
        error: () => this.setClientIP(DefaultIPLocation)
      });
    } else {
      this.setClientIP(ip);
    }
  }

  setClientIP(ip: ICLientData) {
    const criteoPartnerIDs = {
      'IN': 56509,
      'AE': 69550
    };
    this.vars.clientLocationData.next(ip);
    const criteoId = criteoPartnerIDs[ip.country_name] || 66025;
    this.events.gtmPush({ 'CriteoPartnerID': criteoId });
    this.storage.checkFromSession('iplocation', ip);
    this.setCurrency(ip);
  }

  setCurrency(ip: ICLientData) {
    const currencyFromUrl = this.actRoute.snapshot.queryParams.selected_currency;
    const currencyFromSession = this.storage.getFromSession('currency');
    const currencyFromCode = this.getCurrencyFromCountryCode(ip.country_code);
    if (currencyFromUrl) {
      this.util.setCurrency(currencyFromUrl);
    } else if (currencyFromSession) {
      this.util.setCurrency(currencyFromSession);
    } else if (currencyFromCode) {
      this.util.setCurrency(currencyFromCode.currency);
    }
  }

  getCurrencyFromCountryCode(countrycode: string) {
    const currencies = SupportedCurrencies;
    const currency = currencies.find(item => item.code === countrycode);
    if (currency) {
      return currency;
    }
    if (EuropeanCountries.includes(countrycode)) {
      return currencies.find(item => item.code === 'EUR');
    } else if (GulfCountries.includes(countrycode)) {
      return currencies.find(item => item.code === 'SA');
    } else {
      return currencies.find(item => item.code === 'US');
    }
  }

  setUser() {
    const user = this.util.getUser()?.user;
    if (user && !this.vars.isVariableLogin) {
      if (user.isLoggedIn) {
        this.vars.isLoggedIn$.next(user.isLoggedIn);
      }
      this.vars.userData.next(this.util.getUserData());
      const domain = environment.name === 'local' ? 'localhost' : '.ketto.org';
      this.storage.setCookie('is_logged_in', 'true', 365, domain);
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.vars.userData?.unsubscribe();
  }
}
